import measurementIds from '../../config/googleAnalytics.json';

/**
 * Google Analytics 4 via gtag.js.
 *
 * gtag.js itself is loaded by the static tag in public/index.html, which
 * OneTrust only activates after the visitor opts in to Analytics Cookies
 * (C0002). Everything here is queued on window.dataLayer, which gtag.js
 * drains once it loads, so nothing is sent before consent.
 *
 * The measurement ids per environment live in src/config/googleAnalytics.json
 * so that postprocess.js can put the primary id on the tag at build time.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// Mirrors the UA-style event options react-ga4 accepted, so callers are unchanged.
export type UaEventOptions = {
  category: string;
  action: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
  transport?: 'beacon' | 'xhr' | 'image';
};

type InitializeOptions = {
  userId?: string;
};

const environment = process.env.REACT_APP_ENVIRONMENT as keyof typeof measurementIds | undefined;
const trackers: string[] = (environment && measurementIds[environment]) || [];

// gtag.js recognises commands only when pushed as an `arguments` object, so
// this must stay a plain function rather than an arrow function.
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

let initialized = false;

const GaTracker = {
  initialize: ({ userId }: InitializeOptions = {}) => {
    if (initialized || !trackers.length) return;
    initialized = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || gtag;
    gtag('js', new Date());
    trackers.forEach((measurementId) => {
      gtag('config', measurementId, { user_id: userId });
    });
  },
  trackEvent: (event: UaEventOptions, options: Record<string, unknown> = {}) => {
    if (!trackers.length) return;

    if (Object.keys(options).length > 0) {
      gtag('set', options);
    }
    // One gtag event call reaches every configured measurement id.
    gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      non_interaction: event.nonInteraction,
      transport_type: event.transport,
    });
  },
};

export default GaTracker;
